'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Image from 'next/image';
import type { ProductMedia } from '@/lib/products';
import { useTranslation } from '@/context/LanguageContext';
import styles from './ProductMediaCarousel.module.scss';

const IMAGE_INTERVAL = 5000;

interface ProductMediaCarouselProps {
  media: ProductMedia[];
  productName: string;
}

export default function ProductMediaCarousel({
  media,
  productName,
}: ProductMediaCarouselProps) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const currentItem = media[activeIndex];
  const nextIndex = (activeIndex + 1) % media.length;
  const nextItem = media.length > 1 ? media[nextIndex] : null;

  const advanceSlide = useCallback(() => {
    if (media.length <= 1) return;
    setActiveIndex((prev) => (prev + 1) % media.length);
    setIsPlaying(true);
  }, [media.length]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (currentItem.type === 'image' && media.length > 1) {
      timerRef.current = setTimeout(advanceSlide, IMAGE_INTERVAL);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeIndex, currentItem.type, media.length, advanceSlide]);

  const handleVideoEnded = () => {
    if (media.length > 1) {
      advanceSlide();
    } else if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleSlideChange = (index: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setActiveIndex(index);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  };

  return (
    <div className={styles.carousel}>
      <div className={styles.viewport} key={activeIndex}>
        {currentItem.type === 'video' ? (
          <div className={styles.videoSlide}>
            <video
              ref={videoRef}
              className={styles.video}
              autoPlay
              muted
              playsInline
              poster={currentItem.poster}
              onClick={togglePlayPause}
              onEnded={handleVideoEnded}
            >
              <source src={currentItem.src} type="video/mp4" />
            </video>

            <button
              className={`${styles.playPauseBtn} ${!isPlaying ? styles.paused : ''}`}
              onClick={togglePlayPause}
              aria-label={isPlaying ? t.media.pauseVideo : t.media.playVideo}
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>
        ) : (
          <div className={styles.imageSlide}>
            <Image
              src={currentItem.src}
              alt={currentItem.alt || productName}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              priority={activeIndex === 0}
              style={{ objectFit: 'cover' }}
            />
          </div>
        )}
      </div>

      {/* Preload next item */}
      {nextItem && (
        <div className={styles.preload} aria-hidden="true">
          {nextItem.type === 'video' ? (
            <link rel="preload" href={nextItem.src} as="video" />
          ) : (
            <Image
              src={nextItem.src}
              alt=""
              fill
              sizes="1px"
              priority={false}
            />
          )}
        </div>
      )}

      {media.length > 1 && (
        <div className={styles.controls}>
          <div className={styles.dots}>
            {media.map((item, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === activeIndex ? styles.activeDot : ''}`}
                onClick={() => handleSlideChange(index)}
                aria-label={`${item.type === 'video' ? t.media.videoLabel : t.media.imageLabel} ${index + 1}`}
              >
                {item.type === 'video' ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                ) : (
                  <span className={styles.dotInner} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
